import { Quaternion, Vector3 } from 'three'
import { BoxSizeEnum, BoxTypeEnum, ColorType } from '../core/enums'
import type { BoxSpec } from '../core/levelData'
import { convertPosition, convertQuaternion } from '../core/unity'
import type { CharacterAnim } from './characterAnim'
import { busObb, type Obb2D } from './geometry'
import {
  BUS_CAPACITY,
  BUS_CENTRE_Z,
  BUS_HEAD_END_Z,
  BUS_LENGTH,
  BUS_TAIL_END_Z,
  BUS_WIDTH,
  raycastPointsFor,
} from './tuning'

/**
 * Bus lifecycle.
 *
 * `Parked` -> `Dispatching` (driving out of the lot onto the road) -> `OnRoad`
 * (following the spline) -> `Boarding` (stopped at a waiting area) ->
 * `Departing` (running off the end of the spline) -> `Gone`.
 *
 * `Nudging` is the round trip a blocked click makes: the bus rolls forward
 * until its bodywork is `crashStopDistance` off the blocker, both buses jolt,
 * and it drives back to the slot it came from. It ends where it started, in
 * `Parked` — see `GameSession.crash`.
 */
export type BusState =
  | 'Parked'
  | 'Nudging'
  | 'Dispatching'
  /** At the roadside, queued in `SplineController._busRequestQueue`. */
  | 'WaitingToMerge'
  | 'OnRoad'
  | 'Boarding'
  | 'Departing'
  | 'Gone'

let nextBusId = 1

export class Bus {
  readonly id = nextBusId++
  readonly color: ColorType
  readonly size: BoxSizeEnum
  readonly length: number
  readonly capacity: number

  /** `BoxTypeEnum.Hidden` buses reveal their colour when they reach the road. */
  type: BoxTypeEnum
  iceBreakCount: number

  /**
   * `Bus.OpenCover` — the roof panels come off as the bus **pulls out of its
   * slot**, so passengers can drop in.
   *
   * It removes **two of the four** cover transforms: `headCoverTransform`
   * (`Lid_Front`) and `middleCoverTransform` (`Lid_Center`). The dump names all
   * four — `coverTransform`, `headCoverTransform`, `middleCoverTransform`,
   * `tailCoverTransform` — and `OpenCover` only ever calls `SetActive(false)`
   * on the middle two.
   *
   * ⚠️ **Two corrections, both of which this comment carried for a long time.**
   *
   *  - The trigger is `BoxToConveyorMovementStart__0x20fad34.c`:61/:79 — the two
   *    arms of one `if`, as the bus leaves its slot — which `session.ts`:1029
   *    implements. `CheckBusRequests`:303 calls `OpenCover` **again**, and it is
   *    idempotent, but naming it as *the* trigger put the roof coming off a
   *    whole merge later than it does.
   *  - `Lid_Back` does **not** go through `EnterSplineRideMode`:
   *    `__0x20fc5ac.c` never reads 0x48. It is dropped by the accommodation
   *    sequence's `OnComplete` (`PlayAccommodationAnimation`:150-158 →
   *    `b__104_0__0x20fe440.c`), i.e. at the **end of the ring wave**, which is a
   *    different moment entirely — see `busView.tailLidVisible`.
   *
   * 📐 The `SetActive(false)` on `headCoverTransform` is **recovered, not read**:
   * Ghidra dropped that call's arguments at `OpenCover`:27, and only the
   * `middleCoverTransform` call keeps its literal (`(lVar2, 0)` at :79/:84). It
   * is `false` by elimination — `ConveyorBox (M).prefab` ships `Lid_Front` with
   * `m_IsActive: 1` and `OpenCover` is its only writer, so the only state change
   * available is off.
   */
  coverOpen = false

  /**
   * `Bus.busLights` — switched on by `BoxToConveyorMovementStart`, alongside
   * the smoke puff and `OpenCover`, as the bus pulls out of its slot.
   *
   * Previously read as never used: `get_BusLights` has no callers anywhere, so
   * a search for the property came back empty. The field is written directly at
   * `Bus+0x110` instead, which that search could not see.
   */
  lightsOn = false

  /** three.js-space transform. */
  readonly position = new Vector3()
  readonly rotation = new Quaternion()

  /**
   * `BusMovementHandler._startPosition` (0x48) / `_startRotation` (0x54) — the
   * pose `Initialize(targetTransform)` records, and the pose a bus that crashed
   * into a blocker is driven back to.
   *
   * `Initialize` runs from `Bus.InitializeMovement()`, which is what `movable`
   * tracks: layer 0 gets it at level load, an upper elevator layer only once
   * the platform has finished rising. So for a bus that came up on a lift this
   * is the **risen** pose, not the one it was parked at below deck — which is
   * why `GameSession` re-reads it there rather than only in this constructor.
   */
  readonly startPosition = new Vector3()
  readonly startRotation = new Quaternion()

  state: BusState = 'Parked'

  /** Percent along the road spline. Meaningful from `OnRoad` onward. */
  splinePercent = 0
  passengers = 0

  /**
   * `BusSplineFollower._passengers` (0x188) — the characters actually aboard,
   * in boarding order, which is the order `UpdatePassengerPoses` indexes seats
   * by. Kept on the bus rather than the follower because a bus outlives its
   * follower and the riders have to be drawn either way.
   *
   * `passengers` is the count `Bus.AddPassenger` maintains and `IsFull` reads;
   * this list trails it by however many are still mid-jump, exactly as
   * `AreaRuntime.InFlightBoardings` does in the original.
   */
  readonly riders: CharacterAnim[] = []

  /**
   * Whether the riders should be leaning.
   *
   * `BusSplineFollower.LateUpdate` sways everyone aboard when the bus is
   * rolling and stops them when it halts; `DepartAndExit` starts it for the
   * whole busload on the way out.
   */
  ridersSwaying = false

  /** Set while `Dispatching`: where on the spline this bus is merging in. */
  entryPercent = 0

  /**
   * Which elevator layer this bus was generated on, or `-1` for a plain lot
   * box. 🔍 `BusController.GenerateBoxFromElevator` walks
   * `data.layers[i]` and hands every one of them to `_boxes` — the layer index
   * is the only thing that distinguishes them afterwards.
   */
  elevatorLayer = -1

  /**
   * `gameObject.activeSelf`. `GenerateBoxFromElevator` ends each layer with
   * `layer.ToggleBusStates(i == 0)`, and `ToggleBusStates` is nothing but
   * `bus.gameObject.SetActive(isActive)` over the layer's list. So a bus waiting
   * below deck is switched **off**: not drawn, and no collider, so it cannot
   * block anybody in the lot either.
   */
  active = true

  /**
   * `InitializeMovement()` has run, so `BusMovementHandler` exists and
   * `OnMouseDown` can fire.
   *
   * Gated on `i == 0` at level load; an upper layer only gets it from
   * `ElevatorController.OnLayerCompleted`'s `OnComplete`, i.e. **after** the
   * platform has finished rising. Which is why a layer that has just appeared
   * is visible for a moment before it can be clicked.
   */
  movable = true

  /**
   * 🔍 `BusMovementHandler._isInitialized` (0x66) — set **fifteen frames after**
   * `Initialize`, not by it.
   *
   * `Initialize__0x2109db8.c` ends with
   * `UniTask.DelayFrame(0xf, 8).ContinueWith(<Initialize>b__16_0)`; 0xf is 15
   * and 8 is `PlayerLoopTiming.Update`. Everything that makes a bus real
   * happens in that continuation (`<Initialize>b__16_0__0x210b910.c`), in
   * order: `_isInitialized = 1` (0x66), `_isInputActive = 1` (0x64), reparent,
   * `EventBus.Subscribe`, `CatchBlockerBus()`, and only then the pose copy into
   * `_startPosition` (0x48) / `_startRotation` (0x54).
   *
   * `OnMouseDown__0x2109fdc.c` opens with
   * `if (_isInputActive != 1 || _isInitialized != 1) return;` — so for those
   * fifteen frames the bus is drawn, collides, and ignores every tap.
   *
   * Distinct from `movable`, which is whether `InitializeMovement()` ran at
   * all. `movable` is the handler existing; this is the handler waking up.
   */
  initialized = false

  /**
   * Frames left of that wait, ticked down by `GameSession.updateBusInit`.
   *
   * Frames rather than seconds because that is what `UniTask.DelayFrame`
   * counts: on a device running at 45 fps the wall-clock wait is a third
   * longer, and nothing about it is measured in time.
   */
  initFrames = 0

  /**
   * `BusMovementHandler._isInputActive` (0x64) — whether `OnMouseDown` will do
   * anything at all.
   *
   * 🔍 `HandleBoxCantMove` clears it, and the shake sequence's `OnComplete`
   * (`<HandleBoxCantMove>b__22_0`, whose entire body is `_isInputActive = true`)
   * puts it back. So a bus that has just been refused ignores clicks until it
   * has finished jiggling.
   *
   * Separate from `movable`, which is about the handler **existing** at all.
   */
  inputActive = true

  /**
   * `transform.DOPunchRotation`'s current offset, in degrees. Zero except
   * during the "can't move" jiggle.
   */
  readonly shakeEuler = new Vector3()

  /**
   * `DOLocalRotate`'s current offset, in degrees — the pitch a bus takes on
   * while it drives in its slot. Zero except during a lot move.
   *
   * 🔍 Both lot tweens add a static `Vector3 * 7f` to the body's euler over the
   * **first half** of the move, then hand the second half to
   * `DORotateQuaternion(_startRotation)`, which takes it back to level:
   *
   *     MoveBoxToTarget    (`__0x210a800.c`:194)  euler + Vector3.left  * 7f
   *     MoveAfterCollision (`__0x210b3dc.c`:63)   euler + Vector3.right * 7f
   *
   * `Vector3.left` is (−1, 0, 0) and `right` is (+1, 0, 0), so the bus noses
   * **down 7°** as it lunges out of the slot and **up 7°** as it reverses back
   * in — it leans into the direction of travel both ways. The profile is a
   * triangle: 0 → ±7 over the first half, ±7 → 0 over the second.
   *
   * See the `crash` comment in `session.ts` for why this was once written off
   * as unrecoverable.
   */
  readonly leanEuler = new Vector3()

  /**
   * Which of the two exhaust systems is `Play()`ing, if either.
   *
   * 🔍 The original owns these as two separate `ParticleSystem` fields and
   * hands over between them at one exact point:
   *
   *     Bus.BoxToConveyorMovementStart (`__0x20fad34.c`)
   *         smokeParticle.Play()                       // 0x100  → 'puff'
   *     BusSplineFollower.TransitionToMainRoad (`__0x2112084.c`:44-104)
   *         State = OnMainRoad
   *         smokeParticle.Stop()                       // 0x100
   *         smokeTrailParticle.Play()                  // 0x108  → 'trail'
   *
   * They are never both on. The puff is `rateOverTime: 5` in world space — a
   * cloud dropped where the bus stood; the trail is `rateOverDistance: 3`
   * parented to the bus, so it only exists while the bus is moving. See
   * `BUS_PARTICLE_VFX`.
   *
   * `Bus.SetTrailsActive(false)` (from `BusSplineFollower.UpdateInvisibility`)
   * and `Bus.ClearTrails()` (from `LateUpdate` and `PlayFailCrash`) both take
   * this back to `'off'` — a bus that is hidden in the tunnel, or that has just
   * crashed, stops smoking.
   */
  smoke: 'off' | 'puff' | 'trail' = 'off'

  /**
   * Whether `PlayDepartEffects` has fired — the tinted glow and the wind lines.
   *
   * 🔍 `BusSplineFollower.DepartAndExit` (`__0x2112a10.c`:38-63) gates it on
   * `IsFull()`:
   *
   *     if (_bus != null && _bus.IsFull()) _bus.PlayDepartEffects();
   *
   * so a bus removed from the road without filling up gets no send-off. Both
   * systems are `looping: 1` and nothing ever stops them — the bus is destroyed
   * at the end of its exit spline, which takes the systems with it.
   */
  departFxPlaying = false

  /**
   * `Bus.skids` (0x118) — whether the two rear `TrailRenderer`s are emitting.
   *
   * 🔍 `Bus.ToggleSkidEmitting` (`__0x20fcd08.c`). On at `MoveBoxToTarget`:63
   * and `TryMoveToConveyor`:104, off at `<MoveBoxToTarget>b__0`
   * (`__0x210cb38.c`:11) — the lot tween's `OnComplete`. A bus lays rubber for
   * exactly as long as it is rolling inside the lot.
   *
   * ⚠️ `DepartAndExit`:155 also calls `ToggleSkidEmitting(true)`, and it is a
   * **no-op**: `Bus.EnterSplineRideMode` has already detached, destroyed and
   * nulled the whole array by then. See `BUS_SKID` — this is the case where
   * reading the call sites alone gives the wrong answer.
   */
  skidding = false

  /** Waiting area this bus is serving, if any. */
  servingAreaIndex = -1

  private readonly rayLocals: Vector3[]

  constructor(spec: BoxSpec) {
    this.color = spec.color
    this.size = spec.size
    this.type = spec.type
    this.iceBreakCount = spec.iceBreakCount
    this.length = BUS_LENGTH[spec.size] ?? BUS_LENGTH[BoxSizeEnum.Medium]
    this.capacity = BUS_CAPACITY[spec.size] ?? BUS_CAPACITY[BoxSizeEnum.Medium]
    this.rayLocals = raycastPointsFor(spec.size)
    convertPosition(spec.position, this.position)
    convertQuaternion(spec.rotation, this.rotation)
    this.startPosition.copy(this.position)
    this.startRotation.copy(this.rotation)
  }

  get isFull(): boolean {
    return this.passengers >= this.capacity
  }

  /** Colour the player can see. Hidden buses read as unknown until revealed. */
  get visibleColor(): ColorType {
    return this.type === BoxTypeEnum.Hidden ? ColorType.None : this.color
  }

  get occupiesLot(): boolean {
    return this.state === 'Parked' || this.state === 'Nudging'
  }

  get isOnRoad(): boolean {
    return this.state === 'OnRoad' || this.state === 'Boarding' || this.state === 'Departing'
  }

  /** Unit forward on the XZ plane. Unity's local +Z, mirrored to local -Z here. */
  forward(out = new Vector3()): Vector3 {
    out.set(0, 0, -1).applyQuaternion(this.rotation)
    out.y = 0
    const len = out.length()
    return len > 1e-6 ? out.divideScalar(len) : out.set(0, 0, -1)
  }

  obb(): Obb2D {
    return busObb(this.position, this.rotation, BUS_WIDTH, this.length, BUS_CENTRE_Z[this.size] ?? 0)
  }

  /** World-space origins of the three forward rays. */
  rayOrigins(out: Vector3[] = []): Vector3[] {
    out.length = 0
    for (const local of this.rayLocals) {
      out.push(local.clone().applyQuaternion(this.rotation).add(this.position))
    }
    return out
  }

  /**
   * Distance from the prefab root to the front bumper, along forward.
   *
   * `BUS_CENTRE_Z` is how far the body sits behind the root, so this lands on
   * the drawn nose: 5.045 - 1.145 = 3.90 for a Medium, against a mesh front
   * face at 3.88.
   */
  get noseDistance(): number {
    return this.length / 2 + (BUS_CENTRE_Z[this.size] ?? 0)
  }

  /**
   * `Bus.HeadEndPoint` / `TailEndPoint` — the bumpers the decompile measures
   * by. Use these wherever the original names them; `noseDistance` is for
   * drawing and for collision against the bodywork.
   */
  get headEndDistance(): number {
    return BUS_HEAD_END_Z[this.size] ?? this.noseDistance
  }

  get tailEndDistance(): number {
    return BUS_TAIL_END_Z[this.size] ?? this.noseDistance - this.length
  }

  /** Distance from TailEnd to HeadEnd — the bus's own span along the curve. */
  get endPointSpan(): number {
    return this.headEndDistance - this.tailEndDistance
  }
}
